/**
 * The client's screens: home, lobby, the game, and the pages opened by link —
 * `?room=` to take or reclaim a seat, `?watch=` to spectate, `?hotseat=` to play
 * round one screen, `?demo=` to look at a state the engine can reach.
 *
 * The game screen knows a view and where to send a command, nothing about how
 * the command travels, so one screen serves a room and the hot seat alike.
 */
import {
  decisionMaker,
  type Command,
  type GameEvent,
  type PlayerId,
  type TrayIndex,
  type View,
} from '@trajan/engine';
import type { SeatInfo, ServerMessage } from '@trajan/protocol';
import { useEffect, useMemo, useRef, useState } from 'react';
import { demoFromLocation, type Demo } from './board/demo';
import { Area, PromptBar, SeatDot, Stage } from './home/Slab';
import { hotSeatFromLocation, HotSeatScreen, hotSeatView, seatName } from './HotSeatScreen';
import { spliceLog } from './log/log';
import { Sidebar } from './log/Sidebar';
import { DemandStanding, ScoringTable } from './panel/EndScreens';
import { pickTray } from './play/holding';
import { Table } from './play/Table';
import { forgetSeat, rememberSeat, seatToken } from './session';
import { TopBar } from './TopBar';
import { createRoom, GameSocket, type Link } from './ws';

type Screen =
  | { kind: 'home' }
  | {
      kind: 'lobby';
      roomCode: string;
      /** The seat this connection holds, or null when it is only watching. */
      playerId: PlayerId | null;
      seats: SeatInfo[];
      spectatorCode?: string;
    }
  | {
      kind: 'game';
      roomCode: string;
      playerId: PlayerId | null;
      seats: SeatInfo[];
      view: View;
      /** Every event the room has broadcast, in order: the game log. */
      log: GameEvent[];
    }
  /** Opened a room link, with no seat of our own yet: who are you? */
  | { kind: 'naming'; roomCode: string }
  /** The host ended the room. Nothing more will happen in it. */
  | { kind: 'closed'; roomCode: string };

export function App() {
  // `?demo` renders the table from a deterministic engine state, with no server.
  const demo = useMemo(() => demoFromLocation(window.location.search), []);
  // `?hotseat` runs the whole game in this browser, also with no server.
  const hotSeat = useMemo(() => hotSeatFromLocation(window.location.search), []);
  if (demo) return <DemoScreen demo={demo} />;
  if (hotSeat)
    return (
      <HotSeatScreen
        setup={hotSeat}
        render={({ hs, seat, seats, send }) => (
          <Game
            view={hotSeatView(hs)}
            playerId={seat}
            seats={seats}
            log={hs.log}
            sendCommand={send}
          />
        )}
      />
    );
  return <Live />;
}

/**
 * The preview page: the table from a state the engine really reached, with no
 * server behind it. A move made on it has nowhere to go, so the page shows the
 * command instead — which is also how the table is checked without a room.
 */
const DEMO_NAMES = ['Ginny', 'Sam', 'Ali', 'Wren'];
const demoName = (seat: PlayerId) => DEMO_NAMES[seat] ?? `seat ${seat}`;

/** How many entries `?tick` holds back, so there is something left to arrive. */
const TICK_TAIL = 20;

/** With `?tick=ms`, the last entries arrive one at a time, as they would in play. */
function useTickingLog(demo: Demo): GameEvent[] {
  const ms = demo.tick;
  const [shown, setShown] = useState(
    ms === null ? demo.events.length : Math.max(0, demo.events.length - TICK_TAIL),
  );
  useEffect(() => {
    if (ms === null) return;
    const id = setInterval(() => setShown((n) => (n >= demo.events.length ? n : n + 1)), ms);
    return () => clearInterval(id);
  }, [ms, demo.events.length]);
  return ms === null ? demo.events : demo.events.slice(0, shown);
}

function DemoScreen({ demo }: { demo: Demo }) {
  const [sent, setSent] = useState<string | null>(null);
  const [peek, setPeek] = useState<PlayerId | null>(null);
  const [pinned, setPinned] = useState<PlayerId | null>(null);
  const events = useTickingLog(demo);
  const deciding = decisionMaker(demo.view);
  const sidebar = (
    <Sidebar
      view={demo.view}
      seat={demo.seat}
      events={events}
      deciding={deciding}
      nameOf={demoName}
      peek={peek}
      pinned={pinned}
      onPeek={setPeek}
      onPin={setPinned}
    />
  );
  if (demo.only === 'log') {
    return (
      <main className="app">
        <div className="sidebar-solo">{sidebar}</div>
      </main>
    );
  }
  return (
    <main className="app app-game">
      <TopBar view={demo.view} note="preview" />
      <section className="game">
        <div className="game-main">
          <Table
            view={demo.view}
            log={events}
            actor={demo.active ? demo.seat : null}
            shown={peek ?? pinned ?? demo.seat}
            nameOf={demoName}
            onSend={(command) => setSent(JSON.stringify(command))}
            waiting={deciding === null ? 'Nobody is to play.' : `${demoName(deciding)} is to play.`}
            initial={{
              holding:
                demo.active && demo.place !== null
                  ? pickTray(demo.view, demo.seat, demo.place as TrayIndex)
                  : undefined,
              hover: demo.hover !== null ? `tray:${demo.hover}` : undefined,
            }}
            overlay={
              demo.view.phase.kind === 'gameOver' ? (
                <Scores view={demo.view} nameOf={demoName} />
              ) : undefined
            }
          />
          <p className="demo-sent" data-testid="demo-sent">
            {sent ? `would send: ${sent}` : 'nothing sent yet'}
          </p>
        </div>
        {sidebar}
      </section>
    </main>
  );
}

function Live() {
  const socket = useRef(new GameSocket());
  const [screen, setScreen] = useState<Screen>({ kind: 'home' });
  const [name, setName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [watchInput, setWatchInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<Link>('connecting');
  const connected = link === 'open';
  const [watchers, setWatchers] = useState(0);
  // A room is a link: `?room=<code>` seats you (or gets your seat back with the
  // token this browser kept), `?watch=<code>` puts you behind the glass.
  const opened = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return { room: params.get('room'), watch: params.get('watch') };
  }, []);

  useEffect(() => {
    const s = socket.current;
    const off = s.on((msg: ServerMessage) => {
      setError(null);
      switch (msg.type) {
        case 'joined':
          // The token is what gets this seat back after a refresh; the room in
          // the address bar is what makes the page worth refreshing. It is
          // what a reconnection re-sends too — but only then: this connection
          // is already in, and a `join` sent now would be answered with another
          // `joined`, and that one with another `join`, for good.
          rememberSeat(msg.roomCode, msg.sessionToken);
          putRoomInUrl(msg.roomCode);
          s.rejoinWith({
            type: 'join',
            protocol: 1,
            roomCode: msg.roomCode,
            sessionToken: msg.sessionToken,
          });
          setScreen((prev) =>
            // A rejoin lands mid-game; only a first join belongs in the lobby.
            prev.kind === 'game' && prev.roomCode === msg.roomCode
              ? { ...prev, playerId: msg.playerId, seats: msg.seats }
              : {
                  kind: 'lobby',
                  roomCode: msg.roomCode,
                  playerId: msg.playerId,
                  seats: msg.seats,
                  ...(prev.kind === 'lobby' && prev.spectatorCode
                    ? { spectatorCode: prev.spectatorCode }
                    : {}),
                },
          );
          break;
        case 'spectating':
          setScreen({ kind: 'lobby', roomCode: msg.roomCode, playerId: null, seats: msg.seats });
          break;
        case 'watchers':
          setWatchers(msg.count);
          break;
        case 'lobby':
          // The same message says "the seats changed" and "this room is over";
          // the status is what tells them apart. Only a page still in the room
          // is closed by it: one that has gone back to the start can still hear
          // from the room it left — another seat leaving it — and stays put.
          setScreen((prev) =>
            prev.kind !== 'lobby' && prev.kind !== 'game'
              ? prev
              : msg.status === 'abandoned'
                ? { kind: 'closed', roomCode: prev.roomCode }
                : prev.kind === 'lobby'
                  ? { ...prev, seats: msg.seats }
                  : prev,
          );
          break;
        case 'view':
          setScreen((prev) =>
            prev.kind === 'lobby' || prev.kind === 'game'
              ? {
                  kind: 'game',
                  roomCode: prev.roomCode,
                  playerId: prev.playerId,
                  seats: msg.seats,
                  view: msg.view,
                  // The server says where in the log its events belong, so the
                  // first view and every one after it are spliced the same way.
                  log: spliceLog(prev.kind === 'game' ? prev.log : [], msg.logFrom, msg.events).log,
                }
              : prev,
          );
          break;
        case 'rejected':
        case 'error':
          // A token the server does not know is a seat that is gone: forget it,
          // or every refresh from here on fails the same way.
          if (msg.type === 'error' && (msg.code === 'ROOM_CLOSED' || msg.code === 'GAME_STARTED')) {
            if (opened.room) forgetSeat(opened.room);
          }
          // The server's messages are written to be read, so they are shown as
          // they are; the code is for the log, not for the person. Engine and
          // server messages are both lowercase fragments by convention, and a
          // line on its own starts with a capital.
          setError(msg.message.charAt(0).toUpperCase() + msg.message.slice(1));
          break;
      }
    });
    const offStatus = s.onStatus(setLink);
    s.connect();
    // Opening a link is the same as pressing the button it stands for.
    if (opened.watch) s.enter({ type: 'spectate', protocol: 1, spectatorCode: opened.watch });
    else if (opened.room) {
      const token = seatToken(opened.room);
      // A seat we already hold comes straight back; a seat we do not is somebody
      // arriving on a shared link, and they should get to say who they are
      // rather than being seated as "Player 2".
      if (token) s.enter({ type: 'join', protocol: 1, roomCode: opened.room, sessionToken: token });
      else setScreen({ kind: 'naming', roomCode: opened.room });
    }
    return () => {
      off();
      offStatus();
      s.close();
    };
  }, [opened]);

  async function onCreate(playerCount: 2 | 3 | 4) {
    try {
      const { roomCode, spectatorCode } = await createRoom(playerCount);
      setScreen({ kind: 'lobby', roomCode, playerId: 0, seats: [], spectatorCode });
      socket.current.enter({ type: 'join', protocol: 1, roomCode, name });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  /** Take a free seat in a room opened by link, under the name just given. */
  function takeSeat(roomCode: string) {
    socket.current.enter({ type: 'join', protocol: 1, roomCode, name });
  }

  function onJoin() {
    const roomCode = joinCode.trim();
    const token = seatToken(roomCode);
    socket.current.enter({
      type: 'join',
      protocol: 1,
      roomCode,
      name,
      ...(token ? { sessionToken: token } : {}),
    });
  }

  /**
   * Back to the start from a room that is over. Everything that would bring the
   * page back to it goes: the socket stops re-entering it on a reconnection, the
   * seat's token is dropped, and the address no longer names it — so a refresh
   * stays on the start page too.
   */
  function leaveRoom(roomCode: string) {
    socket.current.leave();
    forgetSeat(roomCode);
    takeRoomOutOfUrl();
    setWatchers(0);
    setScreen({ kind: 'home' });
  }

  /** A hot-seat game is a URL, so the same game can always be started again. */
  function startHotSeat(playerCount: 2 | 3 | 4) {
    // This names a game rather than deciding anything inside one, so it comes
    // from the browser rather than the engine's seeded rng.
    const seed = Array.from(crypto.getRandomValues(new Uint8Array(5)), (b) =>
      b.toString(16).padStart(2, '0'),
    ).join('');
    window.location.search = `?hotseat=${seed}&players=${playerCount}`;
  }

  const pill = (
    <span className={connected ? 'pill ok' : 'pill bad'}>
      {link === 'open' ? 'connected' : link === 'connecting' ? 'connecting…' : 'reconnecting…'}
    </span>
  );

  return (
    <main className={screen.kind === 'game' ? 'app app-game' : 'app app-stage'}>
      {screen.kind === 'game' ? (
        <TopBar view={screen.view} note={`room ${screen.roomCode}`}>
          {watchers > 0 && <span className="muted">{watcherLine(watchers)}</span>}
          {pill}
          {screen.playerId === 0 && (
            <EndRoom
              onEnd={() => socket.current.send({ type: 'abandon', roomCode: screen.roomCode })}
            />
          )}
        </TopBar>
      ) : (
        <header className="topbar">
          <h1>Trajan</h1>
          <span className="topbar-where">{STAGE_WHERE[screen.kind]}</span>
          {/* In the text face: a room code is case-sensitive, and Cinzel has no lower case. */}
          {screen.kind !== 'home' && <span className="topbar-note">room {screen.roomCode}</span>}
          <span className="topbar-end">{pill}</span>
        </header>
      )}
      {link === 'reconnecting' && (
        <p className="banner" role="status">
          Lost the connection — trying again. Nothing is lost: the game is on the server, and your
          seat is kept for you.
        </p>
      )}
      {error && <p className="error">{error}</p>}

      {screen.kind === 'home' && (
        <Stage prompt={<PromptBar title="Welcome" hint={homeHint(link, name)} />}>
          <div className="areas areas-home">
            <Area title="Your name" className="area-name">
              <input
                className="inscribed"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ginny"
                aria-label="Your name"
              />
            </Area>
            <Area title="Open a table" glyph="trajan" note="online">
              <p className="area-text">A room on the server, and a link to send each friend.</p>
              <div className="count-tiles">
                {([2, 3, 4] as const).map((n) => (
                  <CountTile
                    key={n}
                    n={n}
                    label={`New ${n}-player game`}
                    disabled={!connected}
                    onClick={() => void onCreate(n)}
                  />
                ))}
              </div>
            </Area>
            <Area title="Hot seat" glyph="cards" note="no server">
              <p className="area-text">
                Everyone round one screen. Each hand is hidden as the screen changes hands.
              </p>
              <div className="count-tiles">
                {([2, 3, 4] as const).map((n) => (
                  <CountTile
                    key={n}
                    n={n}
                    label={`Hot seat, ${n} players`}
                    onClick={() => startHotSeat(n)}
                  />
                ))}
              </div>
            </Area>
            <Area title="Take a seat" glyph="forum">
              <form
                className="code-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (connected && joinCode) onJoin();
                }}
              >
                <input
                  className="inscribed code"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder="room code"
                  aria-label="Room code"
                />
                <button type="submit" className="prompt-go" disabled={!connected || !joinCode}>
                  Join
                </button>
              </form>
            </Area>
            <Area title="Watch a game" glyph="senate">
              <form
                className="code-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!connected || !watchInput) return;
                  socket.current.enter({
                    type: 'spectate',
                    protocol: 1,
                    spectatorCode: watchInput.trim(),
                  });
                }}
              >
                <input
                  className="inscribed code"
                  value={watchInput}
                  onChange={(e) => setWatchInput(e.target.value)}
                  placeholder="spectator code"
                  aria-label="Spectator code"
                />
                <button type="submit" className="prompt-quiet" disabled={!connected || !watchInput}>
                  Watch
                </button>
              </form>
            </Area>
          </div>
        </Stage>
      )}

      {screen.kind === 'naming' && (
        <Stage
          narrow
          prompt={
            <PromptBar
              title="Invited"
              hint="You have been sent a seat at this table. Say who you are and sit down."
              yours
            />
          }
        >
          <Area title="Take a seat" glyph="forum" note={`room ${screen.roomCode}`}>
            <form
              className="code-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (connected) takeSeat(screen.roomCode);
              }}
            >
              <input
                className="inscribed"
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                aria-label="Your name"
              />
              <button type="submit" className="prompt-go" disabled={!connected}>
                Take a seat
              </button>
            </form>
          </Area>
        </Stage>
      )}

      {screen.kind === 'closed' && (
        <Stage
          narrow
          prompt={
            <PromptBar title="Closed" hint="Nothing more will happen in this room.">
              <button className="prompt-go" onClick={() => leaveRoom(screen.roomCode)}>
                Back
              </button>
            </PromptBar>
          }
        >
          <Area title="This room is closed" glyph="senate">
            <p className="area-text">The host ended it. Start a new game, or join another room.</p>
          </Area>
        </Stage>
      )}

      {screen.kind === 'lobby' && (
        <Stage
          prompt={
            <PromptBar
              title={screen.playerId === null ? 'Watching' : 'Gathering'}
              yours={screen.playerId === 0}
              hint={
                screen.playerId === null
                  ? 'You are watching this room. It has not started yet.'
                  : screen.playerId === 0
                    ? 'Send your friends the link; start when everyone is seated.'
                    : 'You are seated. The host starts the game when everyone is here.'
              }
            >
              {screen.playerId === 0 && (
                <>
                  <button
                    className="prompt-quiet"
                    onClick={() =>
                      socket.current.send({ type: 'abandon', roomCode: screen.roomCode })
                    }
                  >
                    End this room
                  </button>
                  <button
                    className="prompt-go"
                    onClick={() =>
                      socket.current.send({ type: 'start', roomCode: screen.roomCode })
                    }
                  >
                    Start
                  </button>
                </>
              )}
            </PromptBar>
          }
        >
          <div className="areas areas-lobby">
            <Area
              title="At the table"
              glyph="senate"
              note={watchers > 0 ? watcherLine(watchers) : `${screen.seats.length} seated`}
            >
              <ul className="players">
                {screen.seats.map((s) => (
                  <li
                    key={s.playerId}
                    className={
                      'player-row' +
                      (s.playerId === screen.playerId ? ' player-row-you' : '') +
                      (s.connected ? '' : ' player-row-away')
                    }
                  >
                    <SeatDot seat={s.playerId} />
                    <span className="player-name">
                      {s.name}
                      {s.playerId === screen.playerId ? ' (you)' : ''}
                    </span>
                    <span className="player-tag">{s.isHost ? 'host' : ''}</span>
                    <span className="player-holdings">
                      {seatName(s.playerId)} · {s.connected ? 'here' : 'disconnected'}
                    </span>
                  </li>
                ))}
              </ul>
            </Area>
            {(screen.playerId !== null || screen.spectatorCode) && (
              <Area title="Invite" glyph="trajan">
                {screen.playerId !== null && (
                  <ShareLink
                    label="Send your friends this link to take a seat"
                    url={roomUrl('room', screen.roomCode)}
                  />
                )}
                {screen.spectatorCode && (
                  <ShareLink
                    label="Anyone with this link can watch"
                    url={roomUrl('watch', screen.spectatorCode)}
                  />
                )}
              </Area>
            )}
          </div>
        </Stage>
      )}

      {screen.kind === 'game' && (
        <Game
          view={screen.view}
          playerId={screen.playerId}
          seats={screen.seats}
          log={screen.log}
          sendCommand={(command) => {
            // A closed socket swallows a send, and a control that does
            // nothing without saying so is worse than one that refuses.
            if (link !== 'open') {
              setError('Not connected — that move did not go anywhere. Trying to reconnect.');
              return;
            }
            socket.current.send({
              type: 'command',
              roomCode: screen.roomCode,
              seq: screen.view.seq,
              command,
            });
          }}
        />
      )}
    </main>
  );
}

/**
 * Put the room in the address bar, so the page is worth refreshing and the URL
 * is worth sending. `replaceState` rather than a navigation: this is the same
 * page, now with a name.
 */
function putRoomInUrl(roomCode: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get('room') === roomCode) return;
  url.searchParams.set('room', roomCode);
  url.searchParams.delete('watch');
  window.history.replaceState(null, '', url);
}

/** The start page's address again: no room to take a seat in, none to watch. */
function takeRoomOutOfUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('room');
  url.searchParams.delete('watch');
  window.history.replaceState(null, '', url);
}

/** Where the top bar says we are, on the pages before a game. */
const STAGE_WHERE: Record<Exclude<Screen['kind'], 'game'>, string> = {
  home: 'Two to four players',
  naming: 'Invited to a table',
  lobby: 'Gathering at the table',
  closed: 'The room is closed',
};

/** "2 people are watching" — the only thing a spectator's presence changes. */
const watcherLine = (n: number) => `${n} ${n === 1 ? 'person is' : 'people are'} watching.`;

const roomUrl = (param: 'room' | 'watch', code: string) =>
  `${window.location.origin}${window.location.pathname}?${param}=${code}`;

/**
 * A link, whole and copyable. A bare code is something to explain; a link is
 * something to paste — and it is the whole credential, so the room is exactly
 * as private as the people it is sent to.
 */
function ShareLink({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="share">
      <p className="area-text">{label}</p>
      <div className="code-row">
        <code className="inscribed">{url}</code>
        <button
          className="prompt-quiet"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/** What the start page's bar says: whether the server is there, and what to do first. */
function homeHint(link: Link, name: string): string {
  if (link !== 'open') return 'Reaching the server… the hot seat plays without it.';
  if (!name.trim()) return 'Write your name, then open a table or take a seat at a friend’s.';
  return `Welcome, ${name.trim()}. Open a table, or take a seat with a code.`;
}

/**
 * A table size as a tile, the way the board shows its Trajan tiles: the number,
 * a token in each seat's colour, and a caption.
 */
function CountTile({
  n,
  label,
  disabled = false,
  onClick,
}: {
  n: 2 | 3 | 4;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="count-tile"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
    >
      <span className="count-tile-n">{n}</span>
      <span className="count-tile-seats">
        {Array.from({ length: n }, (_, seat) => (
          <SeatDot key={seat} seat={seat} />
        ))}
      </span>
      <span className="count-tile-caption">players</span>
    </button>
  );
}

/**
 * Ending a live game is the host's to do and nobody's to do by accident, so the
 * button asks once. No dialog: a dialog stops the page dead if it is ever
 * triggered by something other than a person.
 */
function EndRoom({ onEnd }: { onEnd: () => void }) {
  const [asking, setAsking] = useState(false);
  return (
    <span className="end-room">
      {asking ? (
        <>
          <button className="panel-quiet" onClick={onEnd}>
            Yes, end this room for everyone
          </button>{' '}
          <button className="panel-quiet" onClick={() => setAsking(false)}>
            Keep playing
          </button>
        </>
      ) : (
        <button className="panel-quiet" onClick={() => setAsking(true)}>
          End this room
        </button>
      )}
    </span>
  );
}

/** The final scoring, laid over the table, which can be put aside to look at the board. */
function Scores({ view, nameOf }: { view: View; nameOf: (seat: PlayerId) => string }) {
  const [open, setOpen] = useState(true);
  if (!open) {
    return (
      <button type="button" className="scores-reopen" onClick={() => setOpen(true)}>
        Show the final scores
      </button>
    );
  }
  return (
    <div className="scores" role="dialog" aria-label="Final scores">
      <ScoringTable view={view} nameOf={nameOf} />
      <button type="button" className="prompt-quiet" onClick={() => setOpen(false)}>
        Look at the board
      </button>
    </div>
  );
}

/**
 * The game screen. It knows a view, who is looking at it, and where to send a
 * command — nothing about rooms or sockets, so the same screen serves a seat in
 * an online room and the hot seat, where the browser holds the game itself.
 */
function Game({
  view,
  playerId,
  seats,
  log,
  sendCommand,
}: {
  view: View;
  /** The seat this screen belongs to, or null for a spectator. */
  playerId: PlayerId | null;
  seats: SeatInfo[];
  log: GameEvent[];
  sendCommand: (command: Command) => void;
}) {
  const nameOf = (id: PlayerId) => seats[id]?.name ?? `seat ${id}`;
  const deciding = decisionMaker(view);
  const watching = playerId === null;
  const [peek, setPeek] = useState<PlayerId | null>(null);
  const [pinned, setPinned] = useState<PlayerId | null>(null);
  // A spectator holds no seat, so the tableau on show is whoever is to play —
  // which gives away nothing, because a spectator's view has no hands in it.
  const home = playerId ?? deciding ?? view.current;
  const shown = peek ?? pinned ?? home;
  const myTurn = !watching && deciding === playerId;
  // The seat the game is waiting on, when that seat is not there to play it.
  const away =
    deciding !== null && deciding !== playerId && seats[deciding]?.connected === false
      ? deciding
      : null;
  const waiting =
    deciding === null
      ? 'Nobody is to play.'
      : watching
        ? `${nameOf(deciding)} is playing.`
        : `Waiting for ${nameOf(deciding)}.`;

  return (
    <section className="game">
      <div className="game-main">
        {away !== null && (
          // Nothing is enforcing this — there is no turn timer, by decision —
          // so the honest thing is to say plainly that the game is stopped and
          // who it is stopped on.
          <p className="banner" role="status">
            The game is waiting for {nameOf(away)}, who is disconnected. It carries on the moment
            they are back.
          </p>
        )}
        <Table
          view={view}
          log={log}
          actor={playerId}
          shown={shown}
          nameOf={nameOf}
          onSend={sendCommand}
          waiting={waiting}
          extra={
            myTurn && playerId !== null && view.phase.kind === 'quarterEnd.demands' ? (
              <DemandStanding view={view} seat={playerId} />
            ) : undefined
          }
          overlay={
            view.phase.kind === 'gameOver' ? <Scores view={view} nameOf={nameOf} /> : undefined
          }
        />
        <details className="debug">
          <summary>Raw view (debug)</summary>
          <pre>{JSON.stringify(view, null, 2)}</pre>
        </details>
      </div>
      <Sidebar
        view={view}
        seat={playerId}
        events={log}
        deciding={deciding}
        nameOf={nameOf}
        connected={(id) => seats[id]?.connected ?? true}
        peek={peek}
        pinned={pinned}
        onPeek={setPeek}
        onPin={setPinned}
      />
    </section>
  );
}
